import * as ActionKeys from './ActionKeys';
import dot from 'dot-object';
import * as Ajax from '../classes/AjaxApi';
import * as ErrorCodes from '../classes/ErrorCodes';
import * as ApplicationCache from '../classes/ApplicationCache';
import * as Storage from '../classes/Storage';
import { BRAND } from '../interfaces/IPayload';
import { Dispatch } from 'react';
import { IEmailRequest, IRmoEmailRequest } from '../interfaces/IEmailRequest';
import { IBuyInsurancePayload } from '../interfaces/IBuyInsurancePayload';
import { ICoverageItem } from '../interfaces/ICoverageItem';
import { IEmailPayload } from '../interfaces/IEmailPayload';
import { IEndpoint, IProps } from '../interfaces/IProps';
import { getErrorObject } from '../classes/format-error-response/format-error-object';

export interface IAction {
    type: string;
    payload: object | string | number | boolean | undefined;
}

// Load labels, insurance data and set customer email
export const loadApplicationAppData = async (config: IProps, dispatch: Dispatch<IAction>): Promise<any> => {
    const pageType = config.pageType || 'app';
    try {
        await Promise.all([
            loadResourceBundle(config, dispatch),
            loadAllHippoContentElements(config, pageType, 'part1', dispatch),
            pageType === 'app' ? getOccupations(config, dispatch) : null,
        ]);
        if (pageType === 'app') {
            loadAllHippoContentElements(config, pageType, 'part2', dispatch);
            getDangerousSports(config, dispatch);
        }
        dispatch({
            type: ActionKeys.SET_LOADING_COMPLETE,
            payload: true,
        });
    } catch (error: any) {
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
        config.c9log.error(`Load data failed ${error.message}`, 'Actions', getErrorObject(error));
    }
};

// Fetch resourcebundle from hippo
const loadResourceBundle = async (config: IProps, dispatch: Dispatch<IAction>) => {
    try {
        const labelsResponse = await Ajax.call(config.endpoint.resources.url, config.endpoint.resources.method);
        const labelsCommonResponse = await Ajax.call(
            config.endpoint.resourcesCommon.url,
            config.endpoint.resourcesCommon.method,
        );
        const response = { ...labelsResponse.data, ...labelsCommonResponse.data };
        dispatch({
            type: ActionKeys.SET_LABELS,
            payload: dot.object(response),
        });
    } catch (error) {
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

// Fetch occupations
export const getOccupations = async (config: IProps, dispatch: Dispatch<IAction>) => {
    const endpoint = config.endpoint.occupations;
    try {
        const response = await Ajax.get(endpoint.url, endpoint.headers);
        const occupations = response.data;
        dispatch({
            type: ActionKeys.SET_OCCUPATIONS,
            payload: occupations,
        });
    } catch (error) {
        config.c9log.error(`Loading occupations failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const getDangerousSports = async (config: IProps, dispatch: Dispatch<IAction>) => {
    const endpoint = config.endpoint.sports;
    try {
        const response = await Ajax.get(endpoint.url, endpoint.headers);
        const sports = response.data.map((item: any) => ({
            text: item.text,
            value: item.numValue.toString(),
        }));
        dispatch({
            type: ActionKeys.SET_SPORTS,
            payload: sports,
        });
    } catch (error) {
        config.c9log.error(`Loading list of dangerous sports failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const updateDisabilitySum = async (
    config: IProps,
    disabilitySum: string,
    quoteIdList: any,
    dispatch: Dispatch<IAction>,
) => {
    const putEndpoint = config.endpoint.disabilitySum;
    let updatePackage = {
        brand: BRAND.TOPDANMARK,
        quoteIds: quoteIdList,
        disabilitySum: +disabilitySum,
    };

    try {
        await Ajax.put(putEndpoint.url, updatePackage, putEndpoint.headers);
    } catch (error) {
        config.c9log.error(
            `Updating disability or Loading product packages failed ${error}`,
            'Actions',
            getErrorObject(error),
        );
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const updateDeathSum = async (
    config: IProps,
    deathCompensation: any,
    quoteIdList: any,
    dispatch: Dispatch<IAction>,
) => {
    const putEndpoint = config.endpoint.deathSum;
    let updatePackage = {
        brand: BRAND.TOPDANMARK,
        quoteIds: quoteIdList,
        deathSum: +deathCompensation.value,
        selected: deathCompensation.selected,
    };

    try {
        await Ajax.put(putEndpoint.url, updatePackage, putEndpoint.headers);
    } catch (error) {
        config.c9log.error(
            `Updating deathsum or Loading product packages failed ${error}`,
            'Actions',
            getErrorObject(error),
        );
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const updateDangerousSport = async (
    config: IProps,
    sport: any,
    quoteIdList: any[],
    dispatch: Dispatch<IAction>,
) => {
    const putEndpoint = config.endpoint.dangerousSports;
    let updatePackage = {
        brand: BRAND.TOPDANMARK,
        quoteIds: quoteIdList,
        dangerousSports: +sport.value,
        selected: sport.selected,
    };

    try {
        await Ajax.put(putEndpoint.url, updatePackage, putEndpoint.headers);
    } catch (error) {
        config.c9log.error(
            `Updating dangerous sport or Loading product packages failed ${error}`,
            'Actions',
            getErrorObject(error),
        );
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const getPackageDetails = async (
    endpoint: any,
    productkey: string,
    brand: string,
    quoteIds: any[],
    isLoggedOn: boolean,
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    let quoteIdParam = '';
    const url = isLoggedOn ? endpoint.url.loggedOn : endpoint.url.open;
    quoteIds.forEach((quoteId) => {
        quoteIdParam += `&quoteid=${quoteId}`;
    });

    try {
        const response = await Ajax.get(
            `${url}?productkey=${productkey}&brand=${brand}${quoteIdParam}`,
            endpoint.headers,
        );

        dispatch({
            type: ActionKeys.SET_PACKAGE_DETAILS,
            payload: response.data,
        });

        return true;
    } catch (error) {
        c9log.error(`Loading product packages failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
        return false;
    }
};

export const createPackageOffer = async (
    endpoint: any,
    data: any,
    isLoggedOn: boolean,
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    let quoteIds: any[] = [];
    const url = isLoggedOn ? endpoint.url.loggedOn : endpoint.url.open;
    try {
        const response = await Ajax.post(url, data, endpoint.headers);
        if (response.status === 200 || response.status === 201) {
            dispatch({
                type: ActionKeys.SET_PACKAGES,
                payload: response.data,
            });
            if (response.data) {
                response.data.packages.forEach((pack: any) => {
                    quoteIds.push(pack.quoteId);
                });
            }
        }
    } catch (error: any) {
        c9log.error(`Posting product package failed ${error}`, 'Actions', getErrorObject(error));
        if (
            error?.response?.status === 400 &&
            error?.response?.data.message === `Invalid Customer identified - Customer Marking`
        ) {
            throw new Error(`Invalid Customer identified - Customer Marking`);
        } else {
            dispatch({
                type: ActionKeys.SET_HAS_ERROR,
                payload: { stepOne: true, stepTwo: false },
            });
        }
    }
    return quoteIds;
};

export const buyPackage = async (
    endpoint: IEndpoint,
    data: IBuyInsurancePayload,
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    const payload: IBuyInsurancePayload = {
        productKey: data.productKey,
        brand: 'TD',
        quoteId: data.quoteId,
        emailAddress: data.emailAddress,
        kvhxKey: data.kvhxKey,
        productId: data.productId,
        premiumRate: data.premiumRate,
        packageName: data.packageName,
        eboks: data.eboks,
        accept: data.accept,
        bankRegistrationNumber: data.bankRegistrationNumber,
        bankAccountNumber: data.bankAccountNumber,
    };
    if (data.accept) {
        payload.effectiveDate = data.effectiveDate;
    }
    try {
        const response = await Ajax.put(endpoint.url, payload, endpoint.headers);
        return response.data;
    } catch (error) {
        c9log.error(`Putting buy insurance data failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({
            type: ActionKeys.SET_HAS_ERROR,
            payload: { stepOne: true, stepTwo: false },
        });
    }
};

export const sendEmailPaymentAgent = async (
    endpoint: IEndpoint,
    payload: IEmailRequest,
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    try {
        const response = await Ajax.post(endpoint.url, payload, endpoint.headers);
        return response.data;
    } catch (error) {
        c9log.error(`Posting payment/agent information failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const reloadApplicationStateFromCache = async (config: any, dispatch: Dispatch<IAction>, c9log: any) => {
    try {
        const key = Storage.getSessionItem('onsa-personal-injury-cache-key');
        if (key) {
            const response = await ApplicationCache.restoreFromCache(config, key, config.c9log);
            const state = response.data['object'];

            dispatch({
                type: ActionKeys.RELOAD_STATE,
                payload: JSON.parse(state.data),
            });
            dispatch({
                type: ActionKeys.SET_STATUSINITIAL,
                payload: false,
            });
            dispatch({
                type: ActionKeys.SET_STATE_CHANGE,
                payload: false,
            });
        } else {
            dispatch({
                type: ActionKeys.SET_HAS_ERROR,
                payload: ErrorCodes.ERROR_NO_SESSION,
            });
        }
    } catch (error) {
        c9log.error(`Restoring data from application cache failed: ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const saveApplicationStateToCache = async (config: any, states: any, c9log: any) => {
    const response = await ApplicationCache.saveToCache(
        config,
        {
            data: filterStates(states),
        },
        c9log,
    );
    Storage.setSessionItem('onsa-personal-injury-cache-key', response.data.objectId);
};

export const filterStates = (states: any) => {
    const copy = {
        ...states,
    };
    delete copy.labels;
    delete copy.contentBlocks;
    delete copy.hasError;
    return JSON.stringify(copy);
};

export const getCoverageItems = async (
    endpoint: IEndpoint,
    payload: ICoverageItem[],
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    try {
        // 2022-06-21: MUST not be called with headers, x-top-appname, due to CORS.
        const response = await Ajax.post(endpoint.url, payload, {});
        dispatch({
            type: ActionKeys.SET_COVERAGE_ITEMS,
            payload: response.data,
        });
    } catch (error) {
        c9log.error(`Fetching coverage items failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

const loadAllHippoContentElements = async (
    config: any,
    pageType: string,
    part: string,
    dispatch: Dispatch<IAction>,
) => {
    const elements =
        pageType === 'app' ? config.endpoint.contentBlock.content[part] : config.endpoint.contentBlock.receipt[part];
    const endpoint = config.endpoint.contentBlock;
    const blocks = {};

    await (async () => {
        try {
            Object.keys(elements).forEach(async (element) => {
                const response = await loadHippoContentElement(
                    endpoint,
                    pageType,
                    part,
                    element,
                    dispatch,
                    config.c9log,
                );
                // @ts-ignore
                blocks[element] = response.data;
                dispatch({
                    type: ActionKeys.SET_CONTENT_BLOCK,
                    payload: blocks,
                });
            });
        } catch (error) {
            config.c9log.error(`Loading Hippo content element failed ${error}`, 'Actions');
            dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
        }
    })();
};

export const getStakeHolderInfo = async (endpoint: IEndpoint, dispatch: Function, c9log: any) => {
    try {
        return await Ajax.get(`${endpoint.url}`, endpoint.headers);
    } catch (error) {
        c9log.error(`Loading stakeholder from gti-service failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const clearUserSession = async (endpoint: IEndpoint, dispatch: Dispatch<IAction>, c9log: any) => {
    try {
        return await Ajax.remove(endpoint.url, endpoint.headers);
    } catch (error) {
        c9log.error(`Delete logged in user details failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const loadHippoContentElement = async (
    endpoint: any,
    pageType: string,
    part: string,
    elementName: string,
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    // Ensure to load from receipt template
    const content = pageType === 'app' ? endpoint.content[part][elementName] : endpoint.receipt[part][elementName];
    const endpointUrl = endpoint.host + content;

    try {
        return await Ajax.get(endpointUrl, endpoint.headers);
    } catch (error) {
        c9log.error(`Loading Hippo content element failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
        throw Error(`Failed loading Hippo Content Block ${elementName}`);
    }
};

export const sendEmailRmo = async (
    endpoint: IEndpoint,
    payload: IRmoEmailRequest | IEmailPayload,
    dispatch: Dispatch<IAction>,
    c9log: any,
) => {
    try {
        await Ajax.post(endpoint.url, payload, endpoint.headers);
        return { status: 201 };
    } catch (error) {
        // This is not a fatal error - customer can continue
        c9log.error(`Posting RMO E-mail to Business failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const sendEmailReceipt = async (endpoint: IEndpoint, states: any, dispatch: any, c9log: any) => {
    const payload = {
        event: 'SALES_COMPLETE',
        identifier: 'CONTENT_SALES_COMPLETE',
        ...buildingDataObjectForEmailReceipt(states),
    };

    try {
        await Ajax.post(endpoint.url, payload, endpoint.headers);
        return { status: 201 };
    } catch (error) {
        // This is not a fatal error - customer can continue
        c9log.error(`Posting E-mail to Business and customer failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const getPaymentAgreement = async (
    endpoint: IEndpoint,
    dispatch: Dispatch<IAction>,
    c9log: any,
    quoteId: string,
    productId: string,
) => {
    try {
        return await Ajax.get(`${endpoint.url}?quoteId=${quoteId}&productId=${productId}`, endpoint.headers);
    } catch (error) {
        c9log.error(`get payment agreement failed ${error}`, 'Actions');
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

const buildingDataObjectForEmailReceipt = (states: any) => {
    return {
        bankRegistrationNumber: states.payment.regno.value,
        bankAccountNumber: states.payment.account.value,
        effectiveDate: states.termination.date,
        currentInsuranceCompany: states.termination.company.text,
        cprNumber: states.cpr.value,
        residentialAddress: states.address.address,
        fullName: states.personalInformation.name,
        phoneNumber: states.personalInformation.phone,
        emailAddress: states.personalInformation.email,
        quoteId: '',
    };
};

export const getCustomerMarking = async (endpoints: any, c9log: any) => {
    const endpoint = endpoints.customerMarking;
    try {
        const response = await Ajax.get(`${endpoint.url}?product=injury`, endpoint.headers);
        return response.data;
    } catch (error) {
        c9log.error(`Loading dfim status failed ${error}`, 'Actions', getErrorObject(error));
        return true;
    }
};

//Update customer info in GTI
export const putCustomerInfo = async (endpoints: any, customerId: string, stakeholderInfo: any, c9log: any) => {
    const endpoint = endpoints.updateCustomerInfo;
    try {
        await Ajax.put(`${endpoint.url.replace('{id}', customerId)}`, stakeholderInfo, endpoint.headers);
        return true;
    } catch (error) {
        c9log.error(`Persisting stakeholder information failed ${error}`, 'Actions', getErrorObject(error));
        return false;
    }
};

export const getEboksSubscription = async (
    endpoint: IEndpoint,
    dispatch: Dispatch<IAction>,
    c9log: any,
    customerId: string,
) => {
    try {
        return await Ajax.get(`${endpoint.url}/status?brand=TD&id=${customerId}`, endpoint.headers);
    } catch (error) {
        c9log.error(`get eboks subscription failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const doEboksSubscription = async (
    endpoint: IEndpoint,
    dispatch: Dispatch<IAction>,
    c9log: any,
    customerId: string,
) => {
    try {
        return await Ajax.get(`${endpoint.url}/subscribe?brand=TD&id=${customerId}`, endpoint.headers);
    } catch (error) {
        c9log.error(`do eboks subscription failed ${error}`, 'Actions', getErrorObject(error));
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    }
};

export const fetchGiftCardDetails = async (endpoint: IEndpoint, c9log: any) => {
    try {
        return await Ajax.get(endpoint.url, endpoint.headers);
    } catch (error) {
        c9log.error(`Fetching giftcard details is failed for injury ${error}`, 'Actions', getErrorObject(error));
    }
};

export const getInsuranceStatus = async (endpoint: IEndpoint, c9log: any, cpr: string) => {
    try {
        const response = await Ajax.get(`${endpoint.url}?cprNr=${cpr}`, endpoint.headers);
        return response.data;
    } catch (error) {
        c9log.error(`Loading injury exists status failed ${error}`, 'Actions', getErrorObject(error));
        return false;
    }
};
